import { FormControlLabel, FormGroup, Grid, Switch } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '../../components/shared/FormElements';
import { useAppDispatch } from '../../hooks';
import { setPopAlert } from '../../slices/miscSlice';
import { useAppContext } from '../../Context';
import { ProcessType } from '../../types';
import { usePermissions } from '../../services';

const ViewProductionProcessRules: React.FC<{ active: boolean }> = ({ active }) => {
  const [processes, setProcesses] = useState<Array<ProcessType>>([]);
  const [maxOrders, setMaxOrders] = useState<number | ''>('');
  const [originalMaxOrders, setOriginalMaxOrders] = useState<number | ''>('');
  const [hasLoaded, setHasLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const dispatch = useAppDispatch();
  const { apiCall } = useAppContext();
  const { canLimit_production_processes } = usePermissions(['limit_production_processes']);

  const sortedProcesses = useMemo(() => {
    return [...processes].sort((a, b) => a.sort_order - b.sort_order);
  }, [processes]);

  const loadRules = async () => {
    const settingsResponse = await apiCall(
      'GET',
      'settings/production_process_rules/',
      null,
      '',
      'Error al obtener la configuración de producción',
    );
    if (settingsResponse && settingsResponse.max_orders !== undefined) {
      const parsed = Number(settingsResponse.max_orders);
      const value = Number.isNaN(parsed) ? 4 : parsed;
      setMaxOrders(value);
      setOriginalMaxOrders(value);
    } else {
      setMaxOrders(4);
      setOriginalMaxOrders(4);
    }

    const processesResponse = await apiCall('GET', 'processes/admin/', null, '', 'Error al obtener los procesos');
    if (processesResponse) {
      setProcesses(processesResponse);
    }
  };

  useEffect(() => {
    if (!active || hasLoaded) return;
    loadRules();
    setHasLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, hasLoaded]);

  const handleSaveMaxOrders = async () => {
    if (maxOrders === '' || Number.isNaN(Number(maxOrders))) {
      dispatch(setPopAlert({ alertType: 'error', message: 'La cantidad máxima es requerida', show: true }));
      return;
    }
    const value = Number(maxOrders);
    if (value < 0) {
      dispatch(setPopAlert({ alertType: 'error', message: 'La cantidad máxima no puede ser negativa', show: true }));
      return;
    }
    setSaving(true);
    const response = await apiCall(
      'PUT',
      'settings/production_process_rules/',
      { max_orders: value },
      'Configuración actualizada',
      'Error al actualizar la configuración',
    );
    setSaving(false);
    if (response && response.max_orders !== undefined) {
      setMaxOrders(response.max_orders);
      setOriginalMaxOrders(response.max_orders);
    }
  };

  const handleToggleProcess = async (process: ProcessType) => {
    const nextValue = !process.production_restriction_enabled;
    const previous = processes;
    setProcesses((prev) =>
      prev.map((item) =>
        item.id === process.id
          ? { ...item, production_restriction_enabled: nextValue }
          : item,
      ),
    );
    const response = await apiCall(
      'PUT',
      `processes/admin/${process.id}/`,
      { production_restriction_enabled: nextValue },
      'Proceso actualizado',
      'Error al actualizar el proceso',
    );
    if (!response?.id) {
      setProcesses(previous);
    }
  };

  const hasChanges = maxOrders !== '' && maxOrders !== originalMaxOrders;

  return (
    <div>
      <h3>Reglas de Procesos de Producción</h3>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Cantidad Máxima</div>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <input
              type="number"
              min={0}
              value={maxOrders}
              onChange={(event) => {
                const value = event.target.value;
                setMaxOrders(value === '' ? '' : Number(value));
              }}
              className="settings-input form-control"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {canLimit_production_processes && (
              <Button onClick={handleSaveMaxOrders} disabled={!hasChanges || saving}>
                Guardar
              </Button>
            )}
          </Grid>
        </Grid>
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Procesos Con restricción</div>
        {sortedProcesses.length === 0 && <p>No hay procesos registrados</p>}
        {canLimit_production_processes ? (
          <FormGroup>
            {sortedProcesses.map((process) => (
              <FormControlLabel
                key={process.id}
                control={
                  <Switch
                    checked={Boolean(process.production_restriction_enabled)}
                    onChange={() => handleToggleProcess(process)}
                  />
                }
                label={process.process_name}
              />
            ))}
          </FormGroup>
        ) : (
          <div>
            {sortedProcesses.map((process) => (
              <div key={process.id} style={{ marginBottom: '0.25rem' }}>
                {process.process_name}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewProductionProcessRules;
